/**
 * Sound registry — the index the audio workbench and the gate read.
 *
 * **Adding a sound is one line here.** A sub-agent builds
 * `src/audio/sounds/<thing>.ts`, registers it, auditions it at
 * `/audio.html?sound=<id>`, and runs `npm run check:audio` for the gate.
 */

import { machinerySounds } from './sounds/machinery';
import { blackoutSounds } from './sounds/blackout';
import { bellSounds } from './sounds/bell';
import { breathSounds } from './sounds/breath';
import { cullSounds } from './sounds/cull';
import { dogSounds } from './sounds/dog';
import { foleySounds } from './sounds/foley';
import { herdSounds } from './sounds/herd';
import { outSounds } from './sounds/out';
import { pigSounds } from './sounds/pigs';
import { weatherSounds } from './sounds/weather';
import type { SoundDefinition } from './types';

const SOUNDS: readonly SoundDefinition[] = [
  ...machinerySounds,
  ...breathSounds,
  ...dogSounds,
  ...herdSounds,
  ...pigSounds,
  ...cullSounds,
  ...foleySounds,
  ...weatherSounds,
  ...outSounds,
  ...blackoutSounds,
  ...bellSounds,
  // ── more sounds go here ──────────────────────────────────────────────
  // Not yet written, and deliberately so:
  //   strings-beat-7    — the first music in the game. Outdoors only.
];

export function listSounds(): readonly SoundDefinition[] {
  return SOUNDS;
}

export function getSound(id: string): SoundDefinition | undefined {
  return SOUNDS.find((s) => s.id === id);
}

export function listSoundIds(): string[] {
  return SOUNDS.map((s) => s.id);
}

/**
 * Render a sound offline and return its mono sample data.
 *
 * Shared by the workbench and the gate so both measure exactly what plays.
 * Channels are summed to mono; spatialisation is a scene concern, not a
 * property of the source.
 */
export async function renderSound(
  sound: SoundDefinition,
  sampleRate = 48000,
): Promise<{ samples: Float32Array; sampleRate: number }> {
  const frames = Math.max(Math.ceil(sound.duration * sampleRate), 1);
  const ctx = new OfflineAudioContext(1, frames, sampleRate);
  sound.render(ctx);
  const buffer = await ctx.startRendering();
  return { samples: buffer.getChannelData(0), sampleRate: buffer.sampleRate };
}
